import React from 'react';
import { PiggyBank, LineChart, AlertCircle, Info } from 'lucide-react';

/**
 * Компонент раздела формы для параметров инвестиционного вычета (ИИС)
 */
const InvestmentDeductionSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  investmentTypes 
}) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <PiggyBank className="w-5 h-5 mr-2 text-blue-500" />
        Параметры инвестиционного вычета (ИИС)
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Сумма пополнения ИИС */}
        <div>
          <label htmlFor="investmentAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Сумма пополнения ИИС за год
          </label>
          <input
            type="text"
            id="investmentAmount"
            name="investmentAmount"
            value={formData.investmentAmount}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.investmentAmount ? 'input-error' : ''}`}
            placeholder="Введите сумму взносов"
          />
          {errors.investmentAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.investmentAmount}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Максимальная сумма к вычету: 400 000 ₽ (возврат до 52 000 ₽)
          </p>
        </div>

        {/* Тип инвестиционного вычета */}
        <div>
          <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип инвестиционного вычета
          </legend>
          
          <div className="space-y-4">
            {investmentTypes.map(type => (
              <label key={type.id} className={`flex p-3 border rounded-lg cursor-pointer transition-colors
                ${formData.investmentType === type.id
                  ? 'bg-blue-100 border-blue-300 dark:bg-blue-900/40 dark:border-blue-700'
                  : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700/70'
                }`}>
                <input
                  type="radio"
                  name="investmentType"
                  value={type.id}
                  checked={formData.investmentType === type.id}
                  onChange={handleInputChange}
                  className="form-radio mt-1"
                />
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">
                    {type.name}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {type.description}
                  </span>
                </div>
              </label>
            ))}
          </div>
          
          {errors.investmentType && (
            <p className="mt-2 text-xs text-red-500 dark:text-red-400">
              {errors.investmentType}
            </p>
          )}
        </div>
      </div>

      {/* Уведомление для типа Б */}
      {formData.investmentType === 'typeB' && (
        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            <p className="font-medium">Обратите внимание!</p>
            <p className="text-xs mt-1">
              Для вычета типа Б в нашем калькуляторе не производится расчет возврата НДФЛ, 
              так как этот тип вычета предполагает освобождение от налога на доходы от инвестиций, 
              что зависит от ваших будущих инвестиционных результатов.
            </p>
          </div>
        </div>
      )}

      {/* Информационный блок */}
      <div className="mt-4 flex items-start p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
            О вычетах по ИИС
          </h4>
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <p>
              <strong>Тип А:</strong> Возврат 13% от суммы пополнений ИИС (до 52 000 ₽ в год). 
              Подходит, если вы платите НДФЛ и хотите получить гарантированный доход.
            </p>
            <p>
              <strong>Тип Б:</strong> Освобождение от налога на доход от инвестиций. 
              Выгоден, если ожидаете высокую доходность инвестиций.
            </p>
            <p>
              Для получения вычета необходимо:
            </p>
            <ul className="list-disc pl-5 space-y-0.5 mt-1">
              <li>Иметь открытый ИИС в течение не менее 3 лет</li>
              <li>Не иметь других ИИС и не выводить средства</li>
              <li>Иметь доход, облагаемый по ставке 13% (для типа А)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Предупреждение по типу А */}
      {formData.investmentType === 'typeA' && (
        <div className="mt-4 flex items-start">
          <LineChart className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <p className="font-medium">Совет:</p>
            <p>
              Для максимального использования вычета типа А рекомендуется пополнять ИИС 
              на 400 000 ₽ ежегодно. Если ваш годовой НДФЛ меньше 52 000 ₽, 
              вы сможете вернуть только фактически уплаченную сумму налога.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentDeductionSection;